// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    Assignees,
    DdjDocument,
    DdjDocumentRequirementFields,
    DdjDocumentRequirements,
    DocumentStatus,
    FieldTypes,
    FormDocumentUploadType,
    ImpactPrinterFields,
    InternalDocumentTypeIds,
    PddjQueue,
} from '@interfaces/document.interfaces';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { PdftronInteractionsService } from '@services/pdftron-interactions.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { catchError, first, take, takeWhile, tap } from 'rxjs/operators';
import { applyImpactFields, applyImpactFieldsForXandYTesting, prepareFileForUploadBase64, removeAllAnnotations } from '../../utils/pdf-form-utils';
import { getEditFieldsSections } from '../../utils/utils';
import { UserService } from '@services/user.service';
import { DealsService } from '@services/deals.service';
import { User, UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { AppEvent, EditSection } from '@interfaces/interfaces';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FeatureFlagService } from '@services/feature-flag.service';
import { formDataToJson } from 'src/app/utils/formDataToJson';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { Deal } from '@interfaces/deal.interfaces';
import { jsPDF } from 'jspdf';
import { FitModes } from '../document-form/document-form.interfaces';
import { MatDialog } from '@angular/material/dialog';
import { SplitPdfModalComponent } from '../split-pdf-modal/split-pdf-modal.component';
import { FileSizeErrorPDDJModalComponent } from 'src/app/modals/file-size-error-modal/file-size-error-PDDJ-modal.component';

@Component({
    selector: 'app-pddj-upload',
    templateUrl: './pddj-upload.component.html',
    styleUrls: ['./pddj-upload.component.scss'],
})
export class PddjUploadComponent implements OnInit, AfterViewInit {
    private isComponentAlive = true;

    @ViewChild('pdfViewer', { static: true }) pdfViewerRef: ElementRef;
    @Input() pddjDocumentQueue: PddjQueue[];
    @Input() selectedPddjDocument: PddjQueue;
    displayXandY = new BehaviorSubject({ x: '0', y: '0' });
    assignees = Assignees;
    documents = <DdjDocument[]>[];
    activeDocument = <DdjDocument>{};
    documents$ = this.documentsService.docs$().pipe(
        takeWhile(() => this.isComponentAlive),
        tap((docs) => (this.documents = docs))
    );
    signatureDataAdded = false;
    isImpactEditingXandY = false;
    XandYChangeImpact: any;
    comparedAnnot: any = null;
    startingDisplayOffSet: any = { x: '0', y: '0' };
    impactBackupOffsets: any = null;
    startingOffSet: any = { x: '0', y: '0' };
    endOffSet: any = { x: '0', y: '0' };
    editFields: EditSection[] = [];
    isDisabled = true;
    hasDocAvaiableForSelection = false;
    previousQueueDocumentType: string;
    isInErrorState = false;
    public editFormAction: AppEvent = undefined;
    public addField: any = null;

    public isLoading = false;
    public loadingPhrase: string;
    public currentIndex = 0;
    public currentFile: any;
    public title = 'dealer-create-view';
    public fields: any = {};
    public fileInfo: FormGroup;
    public user: User;
    public deal: any;
    public dealId: number;
    public formDocId: number;
    public formDocName: string;
    public fileName: string;
    public signingAccess: Assignees;
    public activeTab = 'Edit';
    public activeTabIndex = 0;
    public jacketDocumentsSection: EditSection = {
        type: Assignees.dealer,
        label: 'Documents in the Deal Jacket',
        fields: [],
        isOpen: true,
    };
    public availbleFormsSection: EditSection = {
        type: Assignees.dealer,
        label: 'Available F&I Forms on Deal',
        fields: [],
        isOpen: true,
    };
    public formsToAddSection: EditSection = {
        type: Assignees.dealer,
        label: 'Additional F&I Forms to Add',
        fields: [],
        isOpen: true,
    };
    public docsOnJacket: DdjDocument[] = [];
    public availableForms: DdjDocument[] = [];
    public formsToAdd: DdjDocument[] = [];
    public allAvailableForms: DdjDocument[] = [];
    public addFormToDeal = false;
    public allowTesting = false;
    public testingJson: any;
    public clearMouseClickUp = new BehaviorSubject<boolean>(false);
    public clearMouseClickDown = new BehaviorSubject<boolean>(false);
    public clearMouseMove = new BehaviorSubject<boolean>(false);
    public tooltipVisible: boolean = false;
    private largeFileSizeRestriction: boolean = false; //25mb 4/29/24
    private largeFileSizeProhibited: boolean = false; //100mb 4/29/24

    constructor(
        protected readonly activatedRoute: ActivatedRoute,
        protected readonly dealsService: DealsService,
        protected readonly deviceService: DeviceDetectorService,
        protected readonly documentsService: DocumentsService,
        protected readonly errorService: ErrorService,
        protected readonly pits: PdftronInteractionsService,
        protected readonly router: Router,
        protected readonly userService: UserService,
        protected readonly changeDetectorRef: ChangeDetectorRef,
        protected readonly featureFlag: FeatureFlagService,
        private readonly fb: FormBuilder,
        private readonly dialog: MatDialog
    ) {
        this.activatedRoute.params.subscribe((params) => {
            this.dealId = +params.dealId;
        });

        this.allowTesting = this.activatedRoute.snapshot.queryParams.testing === 'true';

        this.documentsService.pddjDocumentQueue$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((queue) => {
            this.pddjDocumentQueue = queue;
        });

        this.fileInfo = this.fb.group({
            fileName: ['', Validators.required],
            isDealerOnly: [false, Validators.required],
            createForm: [false, Validators.required],
        });
    }

    ngOnInit(): void {
        this.editFields = getEditFieldsSections();
        this.deal = this.activatedRoute.snapshot.data.deal;
        this.signingAccess = this.getAssigneeType(this.deal, this.userService.user);

        this.allAvailableForms = (this.activatedRoute.snapshot.data.forms || []).filter((form) => {
            return form.uploadTypeId === FormDocumentUploadType.Print_From_PDF || form.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form;
        });

        this.documentsService
            .docs$()
            .pipe(
                takeWhile(() => this.isComponentAlive),
                tap((docs) => (this.documents = docs))
            )
            .subscribe((docs) => {
                // Reset the arrays each time the docs are reset
                this.availableForms = [];
                this.docsOnJacket = [];

                //loop over documents and put in the right folder
                for (const doc of docs) {
                    if (doc.documentStatusId !== DocumentStatus.Deleted) {
                        // Document is already ready to go on the jacket
                        if (doc.readyForSignatures) {
                            this.docsOnJacket.push(doc);
                        } else if (
                            doc.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form ||
                            doc.uploadTypeId === FormDocumentUploadType.Print_From_PDF
                        ) {
                            this.availableForms.push(doc);
                        }
                    }
                }

                this.isLoading = false;
            });

        //set default drop list to first selection.
        if (this.pddjDocumentQueue.some((x) => x)) {
            this.currentIndex = 0;
            this.selectedPddjDocument = this.pddjDocumentQueue[this.currentIndex];
            this.previousQueueDocumentType = this.selectedPddjDocument.type;
            this.selectedQueuedDocument(this.selectedPddjDocument, true);
        }
    }

    ngAfterViewInit(): void {
        this.isImpactEditingXandY = this.userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.FAndIForms]);
        this.displayXandY.pipe(takeWhile(() => this.isComponentAlive)).subscribe((data) => {
            this.XandYChangeImpact = data;
            this.changeDetectorRef.detectChanges();
        });
    }

    get errorState(): boolean {
        return this.isInErrorState;
    }

    get buyerName(): string {
        return this.deal?.buyer ? `${this.deal?.buyer?.firstName} ${this.deal?.buyer?.lastName}` : 'N/A';
    }

    get documentLabel(): string {
        return this.selectedPddjDocument?.impact ? 'impact' : 'laser';
    }

    get additionalForms(): DdjDocument[] {
        const uploadTypeId =
            this.selectedPddjDocument?.type === 'impact' ? FormDocumentUploadType.Print_From_Impact_Form : FormDocumentUploadType.Print_From_PDF;

        return this.allAvailableForms.filter((form) => {
            return (
                form.uploadTypeId === uploadTypeId &&
                this.docsOnJacket.find((d) => d.templateDocumentId === form.documentId) === undefined &&
                this.availableForms.find((d) => d.templateDocumentId === form.documentId) === undefined
            );
        });
    }

    get canSaveToFormLibrary(): boolean {
        // If they have not selected a form, or they have changed the name of the form
        // they can save to the F&I form library.
        return this.formDocId === undefined || this.formDocName !== this.fileInfo.value.fileName;
    }

    get cannotSplitPdf(): boolean {
        return this.selectedPddjDocument?.type !== 'pdf' || this.formDocId !== undefined;
    }

    showTooltip() {
        this.tooltipVisible = true;
    }

    hideTooltip() {
        this.tooltipVisible = false;
    }

    //prevent large files from adding signatures.
    get largeFileUpload(): boolean {
        return this.largeFileSizeRestriction;
    }

    get fileUploadOver100MB(): boolean {
        return this.largeFileSizeProhibited;
    }

    getFormTypeLabel(doc: DdjDocument): string {
        return doc.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form ? 'impact' : 'laser';
    }

    clearImpactOffset() {
        this.setBackupImpactOffsets();
        this.activeDocument.offsetX = 0;
        this.activeDocument.offsetY = 0;
    }

    async setIsSettingOffset(setting: boolean) {
        if (!this.isImpactEditingXandY) {
            return;
        }

        const allImpactWidgets = this.getImpactAnnotations();

        if (allImpactWidgets.length >= 1) {
            this.startingDisplayOffSet = {
                x: this.impactBackupOffsets?.x ?? 0,
                y: this.impactBackupOffsets?.y ?? 0,
            };

            this.comparedAnnot = allImpactWidgets[0].Id;
            this.startingOffSet = {
                x: allImpactWidgets[0].X ?? 0,
                y: allImpactWidgets[0].Y ?? 0,
            };
        }

        // Set scrolling right away to prevent the jumping around
        this.iframeScrolling('', '', 'scroll');
        // reset parameters that keep track of event listeners
        this.clearMouseMove.next(false);
        this.clearMouseClickDown.next(false);
        this.clearMouseClickUp.next(false);

        const callbackMouseMove = (event) => {
            event.preventDefault();
            const scrollElement = document.getElementById('webviewer-1');
            const getMouseLocation = (e) => {
                const scrollLeft = scrollElement?.scrollLeft || 0;
                const scrollTop = scrollElement?.scrollTop || 0;

                return {
                    x: e.pageX + scrollLeft,
                    y: e.pageY + scrollTop,
                };
            };

            const windowsCoords = getMouseLocation(event);
            this.displayXandY.next({
                x: windowsCoords.x.toString(),
                y: windowsCoords.y.toString(),
            });
        };

        // Listen for when user completed offset handling and remove mouseMove listener
        // Save to variable to prevent "Object Unsubscribed" error if user bounces between tabs
        const clearMouseMove = this.clearMouseMove.subscribe((clear) => {
            if (clear) {
                this.iframeScrolling('', '', '');
                this.pits.pdfTron.Core.documentViewer.removeEventListener('mouseMove', callbackMouseMove);
                clearMouseMove.unsubscribe();
            }
        });

        const callbackMouseClickDown = (event) => {
            event.preventDefault();
            this.iframeScrolling('#FF0000', '', 'hidden');
            const annotManager = this.pits.pdfTron.Core.documentViewer.getAnnotationManager();
            const impactWidgets = this.getImpactAnnotations(annotManager);
            annotManager.selectAnnotations(impactWidgets);
        };

        // Listen for when user completed offset handling and remove mouseLeftDown listener
        // Save to variable to prevent "Object Unsubscribed" error if user bounces between tabs
        const clearMouseClickDown = this.clearMouseClickDown.subscribe((clear) => {
            if (clear) {
                this.iframeScrolling('', '', '');
                this.pits.pdfTron.Core.documentViewer.removeEventListener('mouseLeftDown', callbackMouseClickDown);
                clearMouseClickDown.unsubscribe();
            }
        });

        const callbackMouseClickUp = (event) => {
            event.preventDefault();
            this.iframeScrolling('', '', 'scroll');
            const annotManager = this.pits.pdfTron.Core.documentViewer.getAnnotationManager();
            const selectedAnnots = annotManager.getSelectedAnnotations();

            if (selectedAnnots.length >= 1) {
                const matchedComparedAnnot = selectedAnnots.find((x) => (x.Id = this.comparedAnnot));
                if (matchedComparedAnnot) {
                    this.endOffSet = {
                        x: parseInt(this.startingOffSet.x) - parseInt(matchedComparedAnnot.X),
                        y: parseInt(this.startingOffSet.y) - parseInt(matchedComparedAnnot.Y),
                    };
                }
            }
        };

        // Listen for when user completed offset handling and remove mouseLeftUp listener
        // Save to variable to prevent "Object Unsubscribed" error if user bounces between tabs
        const clearMouseClickUp = this.clearMouseClickUp.subscribe((clear) => {
            if (clear) {
                this.iframeScrolling('', '', '');
                this.pits.pdfTron.Core.documentViewer.removeEventListener('mouseLeftUp', callbackMouseClickUp);
                clearMouseClickUp.unsubscribe();
            }
        });

        this.pits.pdfTron.Core.documentViewer.addEventListener('mouseMove', callbackMouseMove);
        this.pits.pdfTron.Core.documentViewer.addEventListener('mouseLeftDown', callbackMouseClickDown);
        this.pits.pdfTron.Core.documentViewer.addEventListener('mouseLeftUp', callbackMouseClickUp);
    }

    async savePdfImpactXYChanges(event: Event) {
        if (
            (this.isImpactEditingXandY && this.endOffSet.x !== this.startingOffSet.x && this.endOffSet.y !== this.startingOffSet.y) ||
            (this.endOffSet.x !== this.activeDocument.offsetX && this.endOffSet.y !== this.activeDocument.offsetY)
        ) {
            this.isLoading = true;
            this.setActiveDocumentXandY(this.endOffSet);
            this.removeEventListenersFromOffset();
            this.documentsService
                .updateTemplateDocumentInfo$(
                    this.userService.getDealerId(),
                    // Account for F&I forms not currently saved on the deal
                    this.activeDocument.templateDocumentId || this.activeDocument.documentId,
                    this.activeDocument.offsetX,
                    this.activeDocument.offsetY
                )
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw error;
                    })
                )
                .subscribe((newDoc: DdjDocument) => {
                    this.currentFile = {
                        ...this.currentFile,
                        offsetX: newDoc?.offsetX,
                        offsetY: newDoc?.offsetY,
                    };
                });
        }
    }

    getImpactAnnotations(annotManager?: any) {
        if (annotManager === null || annotManager === undefined) {
            annotManager = this.pits.pdfTron.Core.documentViewer.getAnnotationManager();
        }

        const annotations = annotManager.getAnnotationsList();
        const impactWidgets = [];

        annotations.forEach((annot) => {
            // The single PNG image version and the regular impact annotatations
            if ((annot.Subject === 'Stamp' && annot.ImageData.includes('image/png;base64,')) || (annot.Subject === 'Free Text' && annot.custom === undefined)) {
                impactWidgets.push(annot);
            }
        });
        return impactWidgets;
    }

    setActiveDocumentXandY(newOffset) {
        if (newOffset) {
            this.activeDocument.offsetX = parseInt(newOffset.x);
            this.activeDocument.offsetY = parseInt(newOffset.y);
            this.startingOffSet = { x: '0', y: '0' };
            this.endOffSet = { x: '0', y: '0' };
        }
    }

    removeEventListenersFromOffset() {
        this.clearMouseMove.next(true);
        this.clearMouseClickDown.next(true);
        this.clearMouseClickUp.next(true);
    }

    iframeScrolling(styleBorder: string, stylePosition: string, scroll: string) {
        const { documentViewer } = this.pits.pdfTron.Core;
        const scrollView = documentViewer.getScrollViewElement();
        // Commented out as it seemed to be causing some strange behavior
        // This did work at one point, so leaving in for the next PDFTron update...
        // scrollView.style.position = stylePosition;
        scrollView.style.borderColor = styleBorder;
        const doc = scrollView.querySelector('.document:first-child');

        if (doc) {
            doc.style.overflow = scroll;
        }

        documentViewer.scrollViewUpdated();
    }

    preventDocs(doc: DdjDocument): boolean {
        if (this.largeFileSizeRestriction || (doc.readyForSignatures && doc.documentStatusId !== DocumentStatus.Deleted)) {
            return true;
        }

        if (doc.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form && this.selectedPddjDocument?.impact) {
            return false;
        }

        if (doc.uploadTypeId === FormDocumentUploadType.Print_From_PDF && this.selectedPddjDocument?.blob) {
            return false;
        }

        if (doc.uploadTypeId === FormDocumentUploadType.Predefined_Form && this.selectedPddjDocument?.blob) {
            return false;
        }

        return true;
    }

    clearForm() {
        this.formDocId = undefined;
        this.formDocName = undefined;
        this.updateFormWithQueueDoc(this.selectedPddjDocument, false);
        this.activeTabIndex = 0;
    }

    // Adds the F&I Form to the current PDDJ Queue Document
    async selectDoc(selectedDocForm: DdjDocument, addToDeal = false) {
        this.isLoading = true;
        this.formDocId = selectedDocForm.documentId;
        this.formDocName = selectedDocForm.displayName;
        this.updateForm(selectedDocForm);

        // Make sure no fields carry over from a previously selected form
        this.pits.fields = {};
        this.pits.fieldsBkup = {};

        // Reset tab index back to Edit
        this.activeTabIndex = 0;
        this.addFormToDeal = addToDeal;

        let template;

        if (this.selectedPddjDocument.impact && !addToDeal) {
            // Get the document by the formDocId
            // Need this because we are not getting the offset values from the list
            // - Since we are now allowing forms not on the deal we need to have a documentId fallback
            template = await firstValueFrom(
                this.documentsService.getFullTemplateData$(selectedDocForm.templateDocumentId || selectedDocForm.documentId, this.addFormToDeal)
            );
        }

        if (this.selectedPddjDocument.impact && selectedDocForm.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form) {
            this.activeDocument = {
                ...selectedDocForm,
                offsetX: template?.offsetX || selectedDocForm?.offsetX,
                offsetY: template?.offsetY || selectedDocForm?.offsetY,
            };

            const observe = selectedDocForm.templateDocumentId
                ? this.documentsService.getSignedFileByPath$(this.dealId, this.formDocId)
                : this.documentsService.getSignedTemplateFileByDocId$(this.formDocId);

            observe.pipe(takeWhile(() => this.isComponentAlive)).subscribe((docFile) => {
                this.currentFile = {
                    // This is primarily done to make sure the
                    // forms fields make it through okay
                    ...this.activeDocument,
                    ...docFile,
                    offsetX: template?.offsetX || selectedDocForm?.offsetX,
                    offsetY: template?.offsetY || selectedDocForm?.offsetY,
                };
                // The impact data is being applied from the doc-loaded event being returned
                // from the pdf-view component
                this.isLoading = false;
            });
        } else if (this.selectedPddjDocument.blob && selectedDocForm.uploadTypeId === FormDocumentUploadType.Print_From_PDF) {
            this.activeDocument = selectedDocForm;
            const { documentViewer } = this.pits.pdfTron.Core;
            const pageCount = documentViewer.getDocument().getPageCount();

            // Remove any fields from inputFieldSettings for pages past page count
            if (this.activeDocument?.inputFieldSettings) {
                for (const key of Object.keys(this.activeDocument.inputFieldSettings)) {
                    const current = this.activeDocument.inputFieldSettings[key];
                    let pageHeight: number;
                    let pageWidth: number;

                    // Get height and width of current page to determine if the annotations fit on their page
                    if (current.page <= pageCount) {
                        pageHeight = documentViewer.getPageHeight(current.page);
                        pageWidth = documentViewer.getPageWidth(current.page);
                    }

                    // Remove the field from the document if...
                    // - The page the annotation is supposed to be on was not included on the uploaded PDF
                    // - The annotation is outside the bounds of the page it was supposed to be on
                    if (current.page > pageCount || (pageHeight && pageWidth && (current.x > pageWidth || current.y > pageHeight))) {
                        delete this.activeDocument.inputFieldSettings[key];
                    }
                }
            }

            this.pits.fields = this.activeDocument?.inputFieldSettings || {};
            this.pits.fieldsBkup = this.pits.fields;
            await this.pits.showAnnotations$();
            this.signatureDataAdded = true;
            this.isLoading = false;
        }
    }

    // Adds an F&I Form to deal that was not already on the deal
    async selectFormToAdd(selectedForm: DdjDocument) {
        const form = await firstValueFrom(this.documentsService.getFullTemplateData$(selectedForm.documentId, true));
        this.addFormToDeal = true;
        this.selectDoc(form, true);
    }

    updateForm(doc: DdjDocument) {
        this.fileInfo.patchValue({
            fileName: doc.displayName,
            isDealerOnly: doc.requirements?.isDealerOnly,
            createForm: false,
        });
    }

    updateFormWithQueueDoc(doc: PddjQueue, preventDefault: boolean) {
        this.fileInfo.patchValue({
            fileName: preventDefault === true ? '' : doc.name,
            isDealerOnly: false,
            createForm: false,
        });
    }

    setBackupImpactOffsets() {
        if (this.impactBackupOffsets === null) {
            this.impactBackupOffsets = {
                x: this.activeDocument?.offsetX ?? 0,
                y: this.activeDocument?.offsetY ?? 0,
            };
        }
    }

    async selectedQueuedDocument(selectedQueuedDocument: PddjQueue, preventDefault: boolean) {
        if (selectedQueuedDocument) {
            if (selectedQueuedDocument.type === 'pdf') {
                // Make sure the PDF uploaded contains the proper extension to prevent DB issues
                this.currentFile = this.selectedPddjDocument.blob;

                if (this.selectedPddjDocument?.blob?.size) {
                    this.largeFileSizeRestriction = this.selectedPddjDocument?.blob?.size / 1048576 > 25;
                    this.largeFileSizeProhibited = this.selectedPddjDocument?.blob?.size / 1048576 > 100;
                }

                if (this.largeFileSizeRestriction) {
                    this.dialog.open(FileSizeErrorPDDJModalComponent, {
                        width: '700px',
                        closeOnNavigation: false,
                        autoFocus: true,
                        data: {
                            fileSizeProhibited: this.largeFileSizeProhibited,
                        },
                    });
                }
            } else if (selectedQueuedDocument.type === 'impact') {
                // Set initial file as a blank PDF
                this.currentFile = this.createBlankPdfForImpactData(selectedQueuedDocument.impact);
            }

            this.updateFormWithQueueDoc(selectedQueuedDocument, preventDefault);
        }
    }

    public async getSigningAccess() {
        const deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId));
    }

    getAssigneeType(deal: Deal, user: User): Assignees {
        // Allow the salesperson or anyone else at the dealership to sign for the dealer
        if (
            user.email === deal.salesperson.email ||
            (user.dealerId === deal.dealerId && (user.userType == UserTypes.Dealer || user.userType == UserTypes.Internal))
        ) {
            return Assignees.dealer;
        } else if (user.email === deal.buyer.email) {
            return Assignees.buyer;
        } else if (user.email === deal.coBuyer.email) {
            return Assignees.cobuyer;
        } else {
            // If we receive undefined from this function
            // assume the user has no signing priviledges
            return undefined;
        }
    }

    createBlankPdfForImpactData(impactData: any) {
        let doc;
        const impactLength = (impactData.DocumentDataLines || []).length;

        if (impactLength > 0) {
            // Determine the rough height of the new PDF
            let calcHeight = impactData.DocumentDataLines.reduce((total, line) => {
                return total + parseFloat(line.LineFeedVerticalSpacing || 0);
            }, 0);

            // 842 is supposed to be A4 height in points
            if (calcHeight < 842) {
                calcHeight = 842;
            }

            // Determine a rough PDF size based on the amount of data we have
            doc = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                // 595.28 is the A4 width in points unit
                format: [600, calcHeight],
            });
        } else {
            // Default export is A4 paper, portrait, using millimeters for units
            doc = new jsPDF();
        }

        return new Blob([doc.output()], { type: 'application/pdf' });
    }

    async setImpactFields(impactData: ImpactPrinterFields, assignee?: Assignees, document?: DdjDocument): Promise<void> {
        await this.pits.showAnnotations$(assignee);
        const instance = await applyImpactFields(this.pits.pdfTron, impactData, document);
        this.pits.pdfTron = instance;
    }

    setImpactXYOffset(impactData: ImpactPrinterFields, document?: DdjDocument): void {
        // Reset the offset as we can't accurately gauge position if we don't
        // with the current set up
        this.clearImpactOffset();

        this.pits.showAnnotations$(undefined, true).then(async () => {
            await removeAllAnnotations(this.pits.pdfTron);

            const instance = await applyImpactFieldsForXandYTesting(this.pits.pdfTron, impactData, document);

            instance.Core.annotationManager.disableReadOnlyMode();
            instance.Core.Tools.Tool.allowAnnotationsOutsidePage();
            instance.Core.Tools.Tool.disableTextSelection();
            instance.UI.disableTools();
            instance.UI.disableFeatures([instance.UI.Feature.Measurement, instance.UI.Feature.MouseWheelZoom]);
            instance.UI.disableElements(['toolbarGroup-Edit', 'annotationPopup']);
            instance.UI.disableToolDefaultStyleUpdateFromAnnotationPopup();
            this.pits.pdfTron = instance;
            this.pits.pdfTron.UI.setFitMode(FitModes.FitWidth);

            await this.setIsSettingOffset(false);
        });
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'clear-field-event':
                this.addField = null;
                break;
            case 'doc-loaded':
                if (this.selectedPddjDocument.type === 'impact' && this.activeTabIndex === 0) {
                    this.editFormAction = { context: 'set-impact', data: { impact: this.selectedPddjDocument.impact } };
                } else if (this.activeTabIndex === 0) {
                    this.editFormAction = { context: 'set-editing', data: { impact: this.selectedPddjDocument.impact } };
                } else {
                    // New document loaded we want to move over to the Edit tab if not already there
                    this.activeTabIndex = 0;
                }
                this.isLoading = false;
                break;
            case 'isErrorState':
                this.isInErrorState = true;
                break;
            default:
                break;
        }
    }

    editTabChanged(event: any) {
        // Clear out any webviewer updates made to handle impact offset handling
        if (this.activeTab === 'Set Impact Offset' && this.activeTab !== event.tab.textLabel) {
            this.pits.pdfTron.UI.enableTools();
            this.pits.pdfTron.UI.enableFeatures([this.pits.pdfTron.UI.Feature.Measurement, this.pits.pdfTron.UI.Feature.MouseWheelZoom]);
            this.pits.pdfTron.UI.enableElements(['toolbarGroup-Edit', 'annotationPopup']);
            this.pits.pdfTron.UI.enableToolDefaultStyleUpdateFromAnnotationPopup();
            this.removeEventListenersFromOffset();
        }

        // (Going editing and navigating to preview || they are going right to impact offset handling)
        // && and they are not currently in testing mode we check the all annots cleared case
        if (
            ((this.activeTab !== 'Set Impact Offset' && event.tab.textLabel === 'Preview') || event.tab.textLabel === 'Set Impact Offset') &&
            this.editFormAction?.context !== 'testing'
        ) {
            this.pits.handleAllFieldsRemovedCase();
        }

        this.activeTab = event.tab.textLabel;

        if (event.tab.textLabel === 'Edit') {
            this.editFormAction = { context: 'set-editing', data: { impact: this.selectedPddjDocument.impact } };
        } else if (event.tab.textLabel === 'Set Impact Offset') {
            this.pits.pdfTron.Core.annotationManager.disableReadOnlyMode();
            this.setImpactXYOffset(this.selectedPddjDocument.impact, this.activeDocument);
        } else {
            //assumed preview if we get here
            this.editFormAction = { context: 'set-viewing', data: { impact: this.selectedPddjDocument.impact } };
        }
    }

    public setIsViewing(assignee: Assignees = null, removeAnnots = false) {
        this.editFormAction = { context: 'set-viewing', data: { assignee, removeAnnots, impact: this.selectedPddjDocument?.impact } };
    }

    public setIsTesting() {
        this.editFormAction = { context: 'testing', data: { impact: this.selectedPddjDocument?.impact } };
    }

    public toggleEditFieldDisplay(section: EditSection): void {
        section.isOpen = !section.isOpen;
    }

    public addFieldEvent(type: FieldTypes, assignee: Assignees): void {
        this.addField = { fieldType: type, assignee };
    }

    public dragStart(e: DragEvent): void {
        this.pits.dragStart(e);
    }

    public dragOver(e: DragEvent): void {
        this.pits.dragOver(e);
    }

    public dragEnd(e: DragEvent, type: FieldTypes, assignee: Assignees): void {
        this.pits.dragEnd(e, type, assignee);
    }

    async saveAndContinue() {
        this.fileInfo.markAllAsTouched();
        if (this.fileInfo.valid) {
            // Check to see if any documents are already on the deal with the same name
            if (this.docsOnJacket.find((d) => d.displayName === this.fileInfo.value.fileName)) {
                const response = await firstValueFrom(
                    this.dialog
                        .open(ConfirmModalComponent, {
                            width: '700px',
                            data: {
                                title: 'Duplicate Document Name',
                                message: 'There is already a document on the deal with this name. Do you want to continue?',
                                btnText: 'Continue',
                            },
                        })
                        .afterClosed()
                );

                if (response !== true) {
                    return;
                }
            }

            // Verify that there are no buyer/cobuyer fields on a dealer only document
            if (this.fileInfo.value.isDealerOnly && this.pits.isInvalidDealerOnly(this.pits.fields)) {
                this.pits.displayInvalidDealerOnlyMessage();
                return;
            }

            // revert document back to a state without test signatures if present
            if (this.editFormAction?.context === 'testing') {
                await this.pits.showAnnotations$(null, true);
            }

            this.fields = { ...this.pits.fields };

            this.isLoading = true;
            const dataToSave = this.formDocId && !this.addFormToDeal ? await this.prepareFormData() : await this.prepareData();

            if (this.fileInfo.value.createForm) {
                this.loadingPhrase = 'Creating F&I Form...';
                await this.saveDocAsForm(dataToSave);
            }
            this.loadingPhrase = 'Saving Document to Deal...';

            if (this.formDocId && !this.addFormToDeal) {
                this.updateDocumentOnDeal(dataToSave);
            } else {
                // No F&I form selected, or adding a form not on the deal, so we add a new document to the deal
                this.saveDocToDeal(dataToSave);
            }
        }
    }

    private async setDocToBase64Json(includeImpactFields = false): Promise<any> {
        // anything except cleaning a pdf
        const base64 = await prepareFileForUploadBase64(this.pits.pdfTron, includeImpactFields);
        if (base64) {
            const docJson = JSON.stringify({ base64File: base64, filename: 'pddj-upload.pdf' });
            const fileSize = Math.round(base64.replace(/=/g, '').length * 0.75) ?? 0;

            return { docJson, fileSize };
        }

        return null;
    }

    private async prepareData(): Promise<any> {
        const formData: FormData = new FormData();
        const fileData: FormData = new FormData();
        const dealerId = this.userService.getDealerId();

        let fileSize;
        //DEAL FIELDS
        formData.append(
            'dealFields',
            JSON.stringify({
                isNew: this.deal.isNew,
                isUsed: this.deal.isUsed,
                isBuy: this.deal.isBuy,
                isLease: this.deal.isLease,
                isCash: this.deal.isCash,
                isFinance: this.deal.isFinance,
                isLeaseTurnIn: this.deal.isLeaseTurnIn,
                isTradeIn: this.deal.isTradeIn,
            })
        );

        const isDealerOnly =
            this.fileInfo.value.isDealerOnly === undefined || this.fileInfo.value.isDealerOnly === null ? false : this.fileInfo.value.isDealerOnly;

        //REQUIREMENTS
        formData.append(
            'requirements',
            JSON.stringify(<DdjDocumentRequirements>{
                [DdjDocumentRequirementFields.mandatoryDocument]: false,
                [DdjDocumentRequirementFields.isDealerOnly]: isDealerOnly,
                [DdjDocumentRequirementFields.needsBuyerUpload]: false,
                [DdjDocumentRequirementFields.needsCoBuyerUpload]: false,
                [DdjDocumentRequirementFields.needsDealerUpload]: false,
            })
        );

        //PDF
        if (this.currentFile && this.currentFile !== null) {
            // we were embedding the fields, but no longer are. They need to be applied in real-time as needed
            formData.append('pdfFields', JSON.stringify(this.pits.fields));

            // Remove all non-impact fields from the PDF itself
            this.clearAnnotationsFromImpactFile();

            const includeImpactFields = this.selectedPddjDocument.impact !== undefined;
            const docInfo = await this.setDocToBase64Json(includeImpactFields);

            formData.append('file', docInfo?.docJson);
            fileSize = docInfo?.fileSize;

            //User Fields
            formData.append(
                'userFields',
                JSON.stringify({
                    dealerId: dealerId,
                })
            );
        }

        //User Fields
        formData.append(
            'dealerFields',
            JSON.stringify({
                dealerId: dealerId,
            })
        );

        let fileName = this.currentFile.name || `pddj-upload-${this.currentIndex + 1}.pdf`;
        const ext = fileName.split('.').pop();
        if (ext !== 'pdf') {
            fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.pdf';
        }

        //DOCUMENT FIELDS
        formData.append(
            'documentFields',
            JSON.stringify({
                fileName,
                size: fileSize ?? 0,
                displayName: this.fileInfo.value.fileName,
                preamble: this.fileInfo.value.fileName,
                documentId: this.currentFile?.documentId,
                internalDocumentTypeId: this.activeDocument?.internalDocumentTypeId || InternalDocumentTypeIds.Other,
                templateDocumentId: this.activeDocument?.documentId,
                readyForSignatures: true,
                uploadTypeId: this.documentLabel === 'impact' ? FormDocumentUploadType.Print_From_Impact_Form : FormDocumentUploadType.Print_From_PDF,
                systemDocument: false,
                offsetX: this.currentFile?.offsetX || 0,
                offsetY: this.currentFile?.offsetY || 0,
            })
        );

        return { formData, fileData };
    }

    private async prepareFormData(): Promise<any> {
        const fields = this.fileInfo.getRawValue();

        let signingStatus;

        //Not signing but flatten the fields for impact documents.
        switch (this.signingAccess) {
            case Assignees.buyer:
                signingStatus = {
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                };
                break;
            case Assignees.cobuyer:
                signingStatus = {
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                };
                break;
            case Assignees.dealer:
                signingStatus = {
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                };
                break;
            default:
                break;
        }

        //readyForSignatures Flag. Set true once impact printer data is loaded into pdf
        if (this.signatureDataAdded) {
            this.activeDocument.readyForSignatures = true;
        }

        // Remove all non-impact fields from the PDF itself
        this.clearAnnotationsFromImpactFile();

        const includeFields = this.activeDocument.uploadTypeId !== FormDocumentUploadType.Print_From_PDF;
        const base64 = await prepareFileForUploadBase64(this.pits.pdfTron, includeFields);
        const byteLength = Math.round(base64.replace(/=/g, '').length * 0.75) ?? 0;

        let fileName = this.activeDocument?.fileName || 'pddj-upload.pdf';
        const ext = fileName.split('.').pop();
        if (ext !== 'pdf') {
            fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.pdf';
        }

        const data: any = {
            signingStatus: signingStatus,
            pdfFields: this.fields,
            pdf: { file: base64, type: 'application/pdf' },
            documentData: {
                dealerId: this.activeDocument.dealerId,
                dealId: this.dealId,
                documentId: this.formDocId,
                displayName: fields.fileName,
                preamble: this.activeDocument.preamble,
                isDealerOnly: fields.isDealerOnly,
                internalDocumentTypeId: this.activeDocument?.internalDocumentTypeId || InternalDocumentTypeIds.Other,
                templateDocumentId: this.activeDocument?.documentId,
                readyForSignatures: true,
                offsetX: this.activeDocument?.offsetX || 0,
                offsetY: this.activeDocument?.offsetY || 0,
                fileName,
                size: byteLength,
                documentLastUpdateDate: this.activeDocument?.documentLastUpdateDate, //for the time being. needed to compare last server date with this.dateTime in api check. 4/4/24
            },
        };

        return data;
    }

    clearAnnotationsFromImpactFile() {
        const { documentViewer } = this.pits.pdfTron.Core;
        const annotManager = documentViewer.getAnnotationManager();
        const annotationsList = annotManager.getAnnotationsList();
        const toRemove = [];

        annotationsList.forEach((annot, index) => {
            // impact fields have subject of Widget
            if (annot.Subject === 'Free Text') {
                toRemove.push(annot);
            }
        });

        annotManager.deleteAnnotations(toRemove, null);
    }

    async saveDocAsForm(data: any) {
        try {
            await firstValueFrom(this.documentsService.createTemplate$(data.formData));
        } catch (ex) {
            this.errorService.showGenericError();
            this.isLoading = false;
        }
    }

    private updateDocumentOnDeal(data: any) {
        this.documentsService
            .impactFlattenFile$(data)
            .pipe(
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((doc) => {
                if (doc) {
                    this.handlePostSaveBehvaior();
                } else {
                    this.isLoading = false;
                }
            });
    }

    private saveDocToDeal(data: any) {
        this.documentsService
            .createDealDocumentV2$(data.formData, this.deal.dealId, false, true)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe(async (doc) => {
                if (doc) {
                    this.handlePostSaveBehvaior();
                } else {
                    this.isLoading = false;
                }
            });
    }

    private async handlePostSaveBehvaior(goToStatus = false) {
        //reset the document error state when loading new document.
        this.isInErrorState = false;

        this.activeDocument = <DdjDocument>{};
        // At the moment, goToStatus is only true when we skip a document
        // which removes it from the queue, so the index stays the same goclearing
        // to the next document
        if (!goToStatus) {
            this.currentIndex++;
        }

        // Clear out any form that was used on the last document
        this.formDocId = undefined;
        this.addFormToDeal = false;
        this.activeDocument = <DdjDocument>{};
        this.signatureDataAdded = false;
        this.fields = {};
        this.pits.fields = {};
        this.pits.fieldsBkup = {};

        // reset the documents in the left-side nav
        await firstValueFrom(this.documentsService.getDocsList$(this.dealId));

        //reset to next document selection if any
        if (this.currentIndex < this.pddjDocumentQueue.length) {
            this.selectedPddjDocument = this.pddjDocumentQueue[this.currentIndex];
            this.selectedQueuedDocument(this.selectedPddjDocument, true);
        } else if (goToStatus) {
            this.documentsService.pddjDocumentQueue = [];
            this.pddjDocumentQueue = this.documentsService.pddjDocumentQueue;
            this.documentsService.updatePddjQueueCount(false);
            this.router.navigate(['/pddj', 'status']);
        } else {
            this.documentsService.pddjDocumentQueue = [];
            this.pddjDocumentQueue = this.documentsService.pddjDocumentQueue;
            this.documentsService.updatePddjQueueCount(false);
            this.router.navigate(['/pddj', 'done']);
        }

        this.isLoading = false;
    }

    changeDeal() {
        // Clear out any documents that have already been completed in the queue
        this.documentsService.pddjDocumentQueue.splice(0, this.currentIndex);
        this.pddjDocumentQueue = this.documentsService.pddjDocumentQueue;

        // reset index back to 0
        this.currentIndex = 0;

        this.router.navigate(['/pddj', 'deals']);
    }

    skipDocument() {
        // Pull up confirm dialog to make sure the user understands
        // that they are removing the document from the queue
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            width: '700px',
            data: {
                title: 'Are You Sure?',
                message: 'Skipping the document will remove it from the printer queue.',
                btnText: 'Skip Document',
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                this.documentsService.pddjDocumentQueue = this.documentsService.pddjDocumentQueue.filter(
                    (queueJob) => queueJob.name !== this.selectedPddjDocument.name
                );
                this.pddjDocumentQueue = this.documentsService.pddjDocumentQueue;
                this.documentsService.updatePddjQueueCount(false);
                this.handlePostSaveBehvaior(true);
            }
        });
    }

    cancelOffset() {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            width: '700px',
            data: {
                title: 'Are You Sure?',
                message: 'Leaving now will undo any offset changes that have not been saved.',
                btnText: 'Continue',
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                this.activeTabIndex = 0;
                this.clearImpactOffset();
                this.setActiveDocumentXandY(this.impactBackupOffsets);
                this.removeEventListenersFromOffset();
            }
        });
    }

    exitUpload() {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            width: '700px',
            data: {
                title: 'Are You Sure?',
                message: 'Leaving now will remove any documents that have not been saved yet.',
                btnText: 'Skip Documents',
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                this.documentsService.pddjDocumentQueue = [];
                this.pddjDocumentQueue = this.documentsService.pddjDocumentQueue;
                this.documentsService.updatePddjQueueCount(false);
                this.handlePostSaveBehvaior(true);
            }
        });
    }

    async splitPdf() {
        const { documentViewer } = this.pits.pdfTron.Core;
        const doc = documentViewer.getDocument();
        const pageCount = doc.getPageCount();
        const pages = [];

        // Loop over page count and spit up the PDF into pages
        for (let i = 1; i <= pageCount; i++) {
            const data = await doc.extractPages([i]);
            pages.push({
                pageNumber: i,
                data: new Uint8Array(data),
            });
        }

        const dialogRef = this.dialog.open(SplitPdfModalComponent, {
            width: '700px',
            data: {
                pageCount,
                pages,
            },
        });
        dialogRef.afterClosed().subscribe(async (result) => {
            const queue: PddjQueue[] = this.documentsService.pddjDocumentQueue;
            let newQueue = [];
            // If we only have one split in array it means no splits were selected
            // and we'll just have the one document
            if (result?.splits?.length > 1) {
                this.isLoading = true;
                this.loadingPhrase = 'Splitting PDF...';

                // 1. Get the current PDF
                const currentIndex = queue.findIndex((queueJob) => queueJob.name === this.selectedPddjDocument.name);

                if (currentIndex >= 0) {
                    // 2. Add all documents before current document
                    const previousDocs = queue.slice(0, currentIndex);
                    newQueue = newQueue.concat(previousDocs);

                    // 2. Loop over splits and extract the pages from the current PDF
                    for (const [index, split] of result.splits.entries()) {
                        const data = await doc.extractPages(split);
                        const arr = new Uint8Array(data);
                        const blob = new Blob([arr], { type: 'application/pdf' });

                        newQueue.push({
                            blob,
                            impact: null,
                            name: `${this.selectedPddjDocument.name.replace('.pdf', '')}_split-${index + 1}.pdf`,
                            type: 'pdf',
                        });
                    }

                    // 3. Add any documents that come after currentIndex
                    const docsToComplete = queue.slice(currentIndex + 1, queue.length);
                    newQueue = newQueue.concat(docsToComplete);

                    // 4. reformat the PDDJ Document Queue and go to the first split PDF
                    this.documentsService.pddjDocumentQueue = newQueue;
                    this.documentsService.updatePddjQueueCount(false);
                    this.selectedPddjDocument = newQueue[this.currentIndex];
                    this.selectedQueuedDocument(this.selectedPddjDocument, true);
                    this.isLoading = false;
                }
            }
        });
    }

    onFilesSelected(event: any) {
        const files: any[] = Array.from(event.target.files);

        if (files.length > 0) {
            const file = files[0];
            this.selectedPddjDocument = {
                type: file.type === 'application/pdf' ? 'pdf' : 'impact',
                mimeType: file.type,
                name: file.name,
                blob: file,
                impact: file.type === 'application/pdf' ? undefined : {},
            };
            this.selectedQueuedDocument(this.selectedPddjDocument, false);
        }
    }

    handleTestJson() {
        try {
            const impact = JSON.parse(this.testingJson);

            this.selectedPddjDocument = {
                type: 'impact',
                name: 'Impact Testing',
                blob: undefined,
                impact,
            };
            this.selectedQueuedDocument(this.selectedPddjDocument, false);
        } catch (ex) {
            console.error('Could not parse testing JSON.');
        }
    }
}
